import React, { useEffect, useRef } from "react";
import {
  MdCake,
  MdClose,
  MdEmail,
  MdLink,
  MdLocationOn,
  MdPhone,
} from "react-icons/md";
import { useSelector } from "react-redux";
import { prospectsSelector } from "../../store/store";
import { RxAvatar } from "react-icons/rx";
import { Link } from "react-router-dom";
import { IoIosLeaf, IoMdEye } from "react-icons/io";
import { LINKEDIN_URLS } from "../../constants";

type Props = {
  onClose: () => void;
  selfRef?: React.RefObject<HTMLDivElement>;
};

const ProfileModal = (props: Props) => {
  const { currentProspect } = useSelector(prospectsSelector);
  const modalSelfRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        modalSelfRef.current &&
        !modalSelfRef.current.contains(e.target as Node)
      ) {
        props.onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props]);
  return (
    <div className="fixed inset-0 z-50 bg-black/30">
      <div
        ref={modalSelfRef}
        className="absolute right-0 top-0 h-[100dvh] w-[min(560px,calc(100vw-16px))] border-l border-light-grey bg-white shadow-2xl"
      >
        <div className="flex h-[76px] items-center justify-between border-b border-light-grey px-6">
          <h1 className="text-md font-semibold text-dark-black">Lead Details</h1>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-[8px] p-1.5 text-dark-grey hover:bg-primary"
            aria-label="Close lead details"
          >
            <MdClose size={24} />
          </button>
        </div>

        <div className="h-[calc(100dvh-76px)] overflow-y-auto px-4 pb-8 pt-8 sm:px-6">
          <div className="flex flex-col items-center gap-4">
            {currentProspect.image_url ? (
              <img src={currentProspect.image_url} alt="" className="h-28 w-28 rounded-full" />
            ) : (
              <div className="flex h-28 w-28 items-center justify-center rounded-full bg-mainColor/20">
                <RxAvatar className="h-24 w-24 text-mainColor" />
              </div>
            )}
            <div className="flex max-w-full flex-col items-center gap-1 text-center">
              <Link
                onClick={(e) => e.stopPropagation()}
                to={LINKEDIN_URLS.profileBaseUrl + currentProspect.linkedinId}
                target="_blank"
                className="text-xl font-semibold text-dark-black"
              >
                {currentProspect.full_name}
              </Link>
              <p className="text-xs text-dark-grey">{currentProspect.headline || ""}</p>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1 rounded-lg bg-primary p-4">
                <span className="flex items-center justify-between gap-6 py-2">
                  <p className="flex items-center gap-2">
                    <MdEmail size={18} className="text-mainColor" /> Email
                  </p>
                  <p className="text-right text-xs">
                    {currentProspect.contactInfo?.emailAddress || "N/A"}
                  </p>
                </span>
                <span className="flex items-center justify-between gap-6 py-2">
                  <p className="flex items-center gap-2">
                    <MdPhone size={18} className="text-mainColor" /> Phone
                  </p>
                  <p className="text-right text-xs">
                    {currentProspect.contactInfo?.phoneNumbers?.[0]?.number || "N/A"}
                  </p>
                </span>
                <span className="flex items-center justify-between gap-6 py-2">
                  <p className="flex items-center gap-2">
                    <MdLocationOn size={18} className="text-mainColor" /> Location
                  </p>
                  <p className="text-right text-xs">{currentProspect.locality || "N/A"}</p>
                </span>
                <span className="flex items-center justify-between gap-6 py-2">
                  <p className="flex items-center gap-2">
                    <MdCake size={18} className="text-mainColor" /> Birthday
                  </p>
                  <p className="text-right text-xs">
                    {currentProspect.contactInfo?.birthDateOn
                      ? `${currentProspect.contactInfo.birthDateOn.day}/${currentProspect.contactInfo.birthDateOn.month}`
                      : "N/A"}
                  </p>
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <h2 className="text-md font-semibold text-dark-black">Activity</h2>
              {currentProspect.connected === "ACCEPTED" && (
                <div className="rounded-lg bg-primary p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <MdLink size={18} className="text-mainColor" />
                      <span className="font-semibold">Connected</span>
                    </div>
                    <small>You are connected with {currentProspect.full_name}</small>
                  </div>
                </div>
              )}
              {currentProspect.connected === "PENDING" && (
                <div className="rounded-lg bg-primary p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <MdLink size={18} className="text-mainColor" />
                      <span className="font-semibold">Request Sent</span>
                    </div>
                    <small>
                      You sent connection request to{" "}
                      <span className="font-semibold">{currentProspect.full_name}</span> using
                      Innlead
                    </small>
                  </div>
                </div>
              )}
              {currentProspect.messageSent && (
                <div className="rounded-lg bg-primary p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <MdLink size={18} className="text-mainColor" />
                      <span className="font-semibold">Message Sent</span>
                    </div>
                    <small>
                      You sent messages to <span className="font-semibold">{currentProspect.full_name}</span>{" "}
                      using Innlead
                    </small>
                  </div>
                </div>
              )}
              {currentProspect.profileVisited ? (
                <div className="rounded-lg bg-primary p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <IoMdEye size={18} className="text-mainColor" />
                      <span className="font-semibold">Profile Visited</span>
                    </div>
                    <small>You visited this profile using Innlead</small>
                  </div>
                </div>
              ) : (
                currentProspect.connected === "NOT_STARTED" && (
                  <div className="my-2 flex flex-col items-center justify-center rounded-xl bg-white py-6">
                    <IoIosLeaf size={86} className="text-mainColor" />
                    <p className="text-md font-semibold">Oh snap! Nothing to see.</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
